// «Estado de la empresa» surfaces (shared/API.md §43): the Registro card (D) and «Mi equipo hoy» (E)
// in one dialog, the profile's «Registro de la empresa · hoy» block (C) and the reader's own chip.
// The ring itself is drawn by Avatar (`ring`). Only `me.actions` are ever offered; Core re-checks.
import { useContext, useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { Attendance, type AttendanceCommand, type AttendanceMe, type AttendancePerson, type AttendanceState, type LunchChoice } from '../../../core/attendance';
import { Icon, type IconName } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, Dialog, EmptyState, Segmented } from '../../kit/Kit';
import { ShellContext, useShell } from '../../shell/ShellContext';
import { refreshAttendance, useAttendance } from './attendance';
import './attendance.css';

/** The reader's own status in the sidebar; opens the Registro card. Nothing without a Core employee or on a day off. */
export function AttendanceChip({ compact = false }: { compact?: boolean }) {
  const { feed, now } = useAttendance();
  const shell = useContext(ShellContext);
  const me = feed?.me;
  if (!shell || !feed || !me || !feed.workday) return null;
  const line = Attendance.line(me, now.minutes, true);
  const text = compact ? Attendance.short(me, now.minutes) : line;
  return (
    <button
      type="button"
      className={`attendance-chip state-${me.state}${compact ? ' compact' : ''}${Attendance.overdue(me, now.minutes) ? ' late' : ''}`}
      onClick={() => shell.openDialog({ kind: 'attendance', view: 'card' })}
      title={line}
      data-testid="attendance-chip"
    >
      <span className="attendance-dot" aria-hidden />
      <span className="attendance-chip-text">{text}</span>
    </button>
  );
}

/** The Registro card (D) and «Mi equipo hoy» (E), one dialog; «Ver mi equipo» switches in place. */
export function AttendanceDialog({ view: initialView, onClose }: { view: 'card' | 'team'; onClose(): void }) {
  const [view, setView] = useState(initialView);
  const { feed, now } = useAttendance();
  useEffect(() => refreshAttendance(), []);
  const title = view === 'card' ? t('attendance.title') : t('attendance.team.title');
  return (
    <Dialog title={title} onClose={onClose} width={440}>
      {view === 'card' ? (
        feed?.me ? (
          <RegistroCard
            me={feed.me}
            lunchMinutes={feed.lunchMinutes}
            nowMinutes={now.minutes}
            onTeam={Attendance.offersTeam(feed) ? () => setView('team') : null}
          />
        ) : (
          <EmptyState title={t('attendance.failed')} />
        )
      ) : feed ? (
        <TeamView onClose={onClose} />
      ) : (
        <EmptyState title={t('attendance.failed')} />
      )}
    </Dialog>
  );
}

function RegistroCard({ me, lunchMinutes, nowMinutes, onTeam }: { me: AttendanceMe; lunchMinutes: number; nowMinutes: number; onTeam: (() => void) | null }) {
  const [busy, setBusy] = useState<AttendanceCommand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lunch, setLunch] = useState<LunchChoice>('oneHour');
  const [other, setOther] = useState<string>(() => Attendance.wire(Math.min(nowMinutes + 90, 1439)));
  const [leaveBack, setLeaveBack] = useState(() => Attendance.defaultLeaveReturn(nowMinutes));
  const can = (c: AttendanceCommand) => me.actions.includes(c);
  const hadLunch = me.events.some((e) => e.type === 4);
  const otherMinutes = /^\d{2}:\d{2}$/.test(other) ? Number(other.slice(0, 2)) * 60 + Number(other.slice(3)) : null;
  const lunchBack = Attendance.lunchReturn(lunch, nowMinutes, lunchMinutes, otherMinutes);
  const leave = Math.max(leaveBack, nowMinutes + 15);

  const register = async (command: AttendanceCommand, returnAt: number | null = null) => {
    if (busy) return;
    setBusy(command);
    setError(null);
    try {
      const result = await data.call('attendanceRegister', command, returnAt === null ? null : Attendance.wire(returnAt));
      if (!result.ok) setError(result.message ?? t('attendance.failed'));
    } catch {
      setError(t('attendance.failed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="attendance-card">
      <section className="attendance-block day">
        <BlockHead icon="briefcase" label={t('attendance.day.header')}>
          {me.state === 'none'
            ? t('attendance.day.notStarted')
            : me.state === 'closed'
              ? tf('attendance.day.closed', Attendance.hm(me.dayEnd ?? me.since ?? 0))
              : tf('attendance.day.started', Attendance.hm(me.dayStart ?? me.since ?? 0))}
        </BlockHead>
        {can('ci') ? <ActionButton kind="primary" busy={busy === 'ci'} onClick={() => void register('ci')} label={t('attendance.day.in')} /> : null}
        {can('co') ? <ActionButton busy={busy === 'co'} onClick={() => void register('co')} label={t('attendance.day.out')} /> : null}
      </section>

      {me.state === 'lunch' || can('co-meal') || (hadLunch && me.state === 'working') ? (
        <section className="attendance-block lunch">
          <BlockHead icon="forkKnife" label={t('attendance.lunch.header')}>
            {me.state === 'lunch'
              ? Attendance.line(me, nowMinutes)
              : can('co-meal')
                ? lunchBack !== null
                  ? tf('attendance.lunch.backAt', Attendance.hm(lunchBack))
                  : t('attendance.lunch.pickTime')
                : t('attendance.lunch.done')}
          </BlockHead>
          {can('ci-meal') ? <ActionButton kind="primary" busy={busy === 'ci-meal'} onClick={() => void register('ci-meal')} label={t('attendance.lunch.back')} /> : null}
          {can('co-meal') ? (
            <>
              <Segmented<LunchChoice>
                value={lunch}
                onChange={setLunch}
                options={[
                  { value: 'oneHour', label: t('attendance.lunch.oneHour') },
                  { value: 'twoHours', label: t('attendance.lunch.twoHours') },
                  { value: 'other', label: t('attendance.lunch.other') },
                ]}
              />
              {lunch === 'other' ? (
                <label className="attendance-time">
                  <span>{t('attendance.lunch.pickTime')}</span>
                  <input type="time" value={other} onChange={(e) => setOther(e.target.value)} data-testid="attendance-lunch-time" />
                </label>
              ) : null}
              <ActionButton kind="primary" busy={busy === 'co-meal'} disabled={lunchBack === null} onClick={() => void register('co-meal', lunchBack)} label={t('attendance.lunch.go')} />
            </>
          ) : null}
        </section>
      ) : null}

      {me.state === 'leave' || can('co-ext') ? (
        <section className="attendance-block leave">
          <BlockHead icon="doorOut" label={t('attendance.leave.header')}>
            {me.state === 'leave' ? Attendance.line(me, nowMinutes) : t('attendance.leave.estimate')}
          </BlockHead>
          {can('ci-ext') ? <ActionButton kind="primary" busy={busy === 'ci-ext'} onClick={() => void register('ci-ext')} label={t('attendance.leave.back')} /> : null}
          {can('co-ext') ? (
            <div className="attendance-stepper">
              <button type="button" className="icon-button" aria-label={t('attendance.leave.earlier')} onClick={() => setLeaveBack(Attendance.stepLeaveReturn(leave, -30, nowMinutes))}>
                −
              </button>
              <span className="attendance-stepper-value" data-testid="attendance-leave-time">
                {Attendance.hm(leave)}
              </span>
              <button type="button" className="icon-button" aria-label={t('attendance.leave.later')} onClick={() => setLeaveBack(Attendance.stepLeaveReturn(leave, 30, nowMinutes))}>
                +
              </button>
              <ActionButton busy={busy === 'co-ext'} onClick={() => void register('co-ext', leave)} label={t('attendance.leave.go')} />
            </div>
          ) : null}
        </section>
      ) : null}

      {error ? (
        <div className="attendance-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="attendance-foot">
        <span>{t('attendance.visibleToYou')}</span>
        {/* null = this reader has no «Mi equipo hoy» (team_scope `none`): no link. */}
        {onTeam ? (
          <button type="button" className="link-button" onClick={onTeam} data-testid="attendance-team-open">
            {t('attendance.team.open')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function BlockHead({ icon, label, children }: { icon: IconName; label: string; children: React.ReactNode }) {
  return (
    <div className="attendance-block-head">
      <span className="attendance-block-icon" aria-hidden>
        <Icon name={icon} size={17} />
      </span>
      <span className="attendance-block-text">
        <span className="attendance-block-label">{label}</span>
        <span className="attendance-block-value">{children}</span>
      </span>
    </div>
  );
}

function ActionButton({ label, onClick, busy, disabled, kind = 'secondary' }: { label: string; onClick(): void; busy: boolean; disabled?: boolean; kind?: 'primary' | 'secondary' }) {
  return (
    <Button kind={kind} onClick={onClick} disabled={busy || disabled}>
      {busy ? t('attendance.saving') : label}
    </Button>
  );
}

const ORDER: AttendanceState[] = ['working', 'lunch', 'leave', 'none', 'closed'];
const STATE_KEY: Record<AttendanceState, string> = {
  working: 'attendance.state.working',
  lunch: 'attendance.state.lunch',
  leave: 'attendance.state.leave',
  none: 'attendance.state.none',
  closed: 'attendance.state.closed',
};

function TeamView({ onClose }: { onClose(): void }) {
  const shell = useShell();
  const { feed, now } = useAttendance();
  const myArea = feed?.me?.area ?? null;
  const [scope, setScope] = useState<'area' | 'all'>(myArea ? 'area' : 'all');
  const team = useMemo(() => (feed ? Attendance.team(feed, scope === 'area' ? myArea : null, now.minutes) : null), [feed, scope, myArea, now.minutes]);
  if (!feed || !team) return null;
  if (!feed.workday) return <EmptyState title={t('attendance.team.notWorkday')} />;
  const open = (userId: string) => {
    onClose();
    shell.openUser({ userId });
  };
  const section = (title: string, rows: typeof team.attention, testId: string) => (
    <section className="attendance-team-section" data-testid={testId}>
      <h3>{title}</h3>
      {rows.length ? (
        rows.map((r) => (
          <button key={r.userId} type="button" className="attendance-team-row" onClick={() => open(r.userId)}>
            <Avatar userId={r.userId} name={r.name} size={30} ring />
            <span className="attendance-team-name">{r.name}</span>
            <span className={`attendance-team-line state-${r.state}`}>{r.line}</span>
          </button>
        ))
      ) : (
        <div className="attendance-team-empty">{t('attendance.team.empty')}</div>
      )}
    </section>
  );
  return (
    <div className="attendance-team">
      {/* The org-chart team (`org`) is already the list: no área / company toggle. */}
      {myArea && feed?.teamScope !== 'org' ? (
        <Segmented<'area' | 'all'>
          value={scope}
          onChange={setScope}
          options={[
            { value: 'area', label: feed.me?.areaName ?? t('attendance.team.myArea') },
            { value: 'all', label: t('attendance.team.everyone') },
          ]}
        />
      ) : null}
      <div className="attendance-counts">
        {ORDER.map((s) => (
          <div key={s} className={`attendance-count state-${s}`}>
            <b>{team.counts[s]}</b>
            <span>{t(STATE_KEY[s])}</span>
          </div>
        ))}
      </div>
      {section(t('attendance.team.attention'), team.attention, 'attendance-team-attention')}
      {section(t('attendance.team.away'), team.away, 'attendance-team-away')}
      {section(t('attendance.team.working'), team.working, 'attendance-team-working')}
    </div>
  );
}

/** The profile's «Registro de la empresa · hoy» (C). Nothing on a day off or for an unknown person. */
export function AttendanceBlock({ userId }: { userId: string }) {
  const { feed, now } = useAttendance();
  const person: AttendancePerson | undefined = feed?.people[userId];
  if (!feed || !feed.workday || !person) return null;
  const segments = Attendance.segments(person, now.minutes);
  return (
    <section className="attendance-profile" data-testid="attendance-profile">
      <div className="attendance-profile-head">
        <span>{t('attendance.profile.title')}</span>
        <span className={`attendance-pill state-${person.state}${Attendance.overdue(person, now.minutes) ? ' late' : ''}`}>{Attendance.line(person, now.minutes)}</span>
      </div>
      {segments.length ? (
        <>
          <div className="attendance-bar" aria-hidden>
            {segments.map((s, i) => {
              const span = Attendance.barSpan(s.from, s.to);
              return <span key={i} className={`attendance-bar-seg state-${s.state}`} style={{ left: `${span.left * 100}%`, width: `${span.width * 100}%` }} />;
            })}
          </div>
          <div className="attendance-bar-scale" aria-hidden>
            <span>7:00</span>
            <span>11:00</span>
            <span>15:00</span>
            <span>20:00</span>
          </div>
        </>
      ) : null}
      {person.events.length ? (
        <ol className="attendance-events">
          {person.events.map((e, i) => (
            <li key={i}>
              <span className="attendance-event-time">{Attendance.hm(e.at)}</span>
              <span className={`attendance-event-dot type-${e.type}`} aria-hidden />
              <span>{Attendance.eventLabel(e)}</span>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
